// @ts-ignore - This import is needed for Supabase migration
import { supabase } from '@/lib/supabase';
import { Fragment, useEffect, useState, useMemo } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { Tag, CreditCard, User, Plus, Check, Calendar, ChevronDown, Sparkles, Loader2, Camera } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { useTagsStore } from '@/store/tagsStore';
import { useAccountsStore } from '@/store/accountsStore';
import { useCategoriesStore } from '@/store/categoriesStore';
import { usePayeesStore } from '@/store/payeesStore';
import { useRulesStore } from '@/store/rulesStore';
import { useTransactionStore } from '@/store/transactionsStore';
import { format } from 'date-fns';
import { AttachmentUpload } from './AttachmentUpload';
import { v4 as uuidv4 } from 'uuid';
import { deleteTransactionAttachment, uploadAttachment } from '@/utils/fileUpload';
import { compressReceiptImage } from '@/utils/compressReceiptImage';
import {
  Account,
  TransactionType,
  Attachment
} from '@/types/base';
import { useCurrenciesStore } from '@/store/currenciesStore';
import { convertCurrency } from '@/services/exchangeRatesService';
import { useCurrencyStore, parseTransactionText, parseReceiptImage } from '@flows.cash/core';
import { useTransactionStore as useCoreTransactionStore } from '@flows.cash/core';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateTransactions } from '@/utils/queryUtils';
import { toast } from "sonner";
import { TagSelect } from './TagSelect';
import { ExchangeRateInput } from './ExchangeRateInput';
import { StyledModal, StyledModalButton } from '@/components/ui/styled-modal';
import { checkForDuplicates, checkForDuplicatesOptimized, DuplicateCheckResult } from '@flows.cash/core';
import { DuplicateConfirmation } from './DuplicateConfirmation';

// Currency symbol mapping
const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  TRY: '₺',
  INR: '₹',
  CNY: '¥',
  RUB: '₽',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  SEK: 'kr',
  NOK: 'kr',
  PLN: 'zł',
  BRL: 'R$',
  MXN: '$',
  ZAR: 'R',
  KRW: '₩',
  SGD: 'S$',
  HKD: 'HK$',
  NZD: 'NZ$',
  // Cryptocurrencies
  BTC: '₿',
  ETH: 'Ξ',
  USDT: '₮',
  // Precious Metals
  XAU: 'Au',
  XAG: 'Ag',
  XPT: 'Pt',
  XPD: 'Pd',
  XRH: 'Rh'
};

// Helper function to get currency symbol from account
const getCurrencySymbol = (accounts: Account[], accountId: string): string => {
  if (!accountId || !accounts.length) return '$';
  
  const account = accounts.find(acc => acc.id === accountId);
  if (!account?.currency) return '$';
  
  return CURRENCY_SYMBOLS[account.currency] || account.currency;
};

// Define types for the payee and tag objects
interface Payee {
  id: string;
  name: string;
  type: string;
  user_id: string;
  category?: string;
  created_at?: string;
  updated_at?: string;
}

interface Tag {
  id: string;
  name: string;
  color: string;
  user_id: string;
  created_at?: string;
  updated_at?: string;
}

interface AddTransactionProps {
  onClose?: () => void;
  defaultType?: 'income' | 'expense' | 'transfer';
}

interface FormData {
  amount: string;
  date: string;
  description: string;
  category: string;
  categoryId: string;
  accountId: string;
  payee: string;
  payeeName?: string;
  notes: string;
  tags: string[];
  attachments: Attachment[];
  hasAttachment: boolean;
  type: TransactionType;
  status: 'completed' | 'pending';
  fromAccountId?: string;
  toAccountId?: string;
  fromAccountName?: string;
  toAccountName?: string;
  exchangeRate?: number;
  currency?: string;
}

// Extend WebAccountsState to include the missing properties
interface ExtendedWebAccountsState {
  accounts: Account[];
  fetchAccounts: (userId?: string) => Promise<Account[]>;
  loading: boolean;
  error: string | null;
  addAccount: (account: Omit<Account, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => Promise<string | null>;
  updateAccount: (id: string, account: Partial<Account>) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  refreshAccountBalance: (accountId: string) => Promise<number | null>;
  refreshAllBalances: () => Promise<void>;
}

export default function AddTransaction({ onClose, defaultType }: AddTransactionProps) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const coreTransactionStore = useCoreTransactionStore();
  const { fetchTransactions } = useTransactionStore();
  // @ts-ignore - Using extended type for accounts store
  const { accounts, fetchAccounts, loading: accountsLoading, error: accountsError } = useAccountsStore() as ExtendedWebAccountsState;
  const { categories, fetchCategories } = useCategoriesStore();
  const { fetchTags, addTag } = useTagsStore();
  const { payees, fetchPayees, addPayee } = usePayeesStore();
  const { rules, fetchRules, applyRules } = useRulesStore();
  const { currencies, exchangeRates, fetchExchangeRates } = useCurrenciesStore();
  const { convertAmount: coreConvertAmount, rates: coreRates, updateRates: updateCoreRates } = useCurrencyStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddPayeeModalOpen, setIsAddPayeeModalOpen] = useState(false);
  const [isAddTagModalOpen, setIsAddTagModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPayeeName, setNewPayeeName] = useState('');
  const [isAddingPayee] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState('#6366F1');
  const [isAddingTag] = useState(false);
  const [categoryQuery, setCategoryQuery] = useState('');
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [payeeQuery, setPayeeQuery] = useState('');
  const [isPayeeOpen, setIsPayeeOpen] = useState(false);
  const [duplicateResult, setDuplicateResult] = useState<DuplicateCheckResult | null>(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingTransaction, setPendingTransaction] = useState<any>(null);

  useEffect(() => {
    // Fetch initial data when component mounts
    const loadData = async () => {
      try {
        if (user?.id) {
          console.log('Fetching data for AddTransaction component...');
          
          await Promise.all([
            fetchAccounts(),
            fetchCategories(user.id),
            fetchTags(user.id),
            fetchPayees(user.id),
            fetchRules(), // Add explicit call to fetch rules
            fetchExchangeRates()
          ]);
          
          console.log('Data fetched successfully, rules count:', rules.length);
          console.log('All rules:', rules);
          console.log('Active rules:', rules.filter(rule => rule.active));
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Remove function dependencies to prevent infinite loops

  const handlePayeeOpen = () => {
    setIsPayeeOpen(true);
    setIsCategoryOpen(false);
  };

  useEffect(() => {
    if (!isCategoryOpen) setCategoryQuery('');
  }, [isCategoryOpen]);

  useEffect(() => {
    if (!isPayeeOpen) setPayeeQuery('');
  }, [isPayeeOpen]);

  // Handle outside clicks for payee dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.payee-dropdown-container')) {
        setIsPayeeOpen(false);
      }
    };

    if (isPayeeOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isPayeeOpen]);

  // Get default account
  const defaultAccount = accounts?.length > 0 ? accounts.find((account: Account) => account.isDefault) : undefined;

  const [formData, setFormData] = useState<FormData>({
    amount: '',
    date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    description: '',
    category: '',
    categoryId: '',
    accountId: defaultAccount?.id || '',
    payee: '',
    payeeName: '',
    notes: '',
    tags: [],
    attachments: [],
    hasAttachment: false,
    type: 'expense',
    status: 'completed',
    exchangeRate: 1
  });

  // Update accountId when accounts change (in case default account is loaded after initial render)
  useEffect(() => {
    if (!formData.accountId && defaultAccount?.id) {
      setFormData(prev => ({
        ...prev,
        accountId: defaultAccount.id
      }));
    }
  }, [accounts, defaultAccount]);

  // Auto-fetch exchange rates when accounts change for transfers
  useEffect(() => {
    const autoFetchExchangeRate = async () => {
      if (formData.type !== 'transfer' || !formData.accountId || !formData.toAccountId) {
        return;
      }

      const fromAccount = accounts.find((acc: Account) => acc.id === formData.accountId);
      const toAccount = accounts.find((acc: Account) => acc.id === formData.toAccountId);
      
      if (!fromAccount?.currency || !toAccount?.currency || fromAccount.currency === toAccount.currency) {
        // Same currency, set rate to 1
        setFormData(prev => ({ ...prev, exchangeRate: 1 }));
        return;
      }

      try {
        // Ensure core store has rates
        if (!coreRates || !coreRates.rates || Object.keys(coreRates.rates).length === 0) {
          await updateCoreRates();
        }
        
        let rate: number;
        try {
          rate = coreConvertAmount(1, fromAccount.currency, toAccount.currency);
        } catch (error) {
          // Fallback to the legacy service
          if (!exchangeRates) {
            await fetchExchangeRates();
          }
          rate = convertCurrency(1, fromAccount.currency, toAccount.currency, exchangeRates || {});
        }

        if (rate && !isNaN(rate)) {
          const roundedRate = Math.round(rate * 1000000) / 1000000; // 6 decimal places
          setFormData(prev => ({ ...prev, exchangeRate: roundedRate }));
        }
      } catch (error) {
        console.error('Error auto-fetching exchange rate:', error);
      }
    };

    autoFetchExchangeRate();
  }, [formData.type, formData.accountId, formData.toAccountId, accounts, coreConvertAmount, updateCoreRates, exchangeRates, fetchExchangeRates]);

  const [errors, setErrors] = useState<Record<string, string>>({});

  // AI quick-add state
  const [aiInput, setAiInput] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiBanner, setAiBanner] = useState<'success' | 'low' | null>(null);
  const [aiCurrencyHint, setAiCurrencyHint] = useState<string | null>(null);
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set());
  // AI-first staging: 'describe' (just the NL box) -> 'review' (summary card)
  // -> save; 'form' is the full manual/edit form.
  const [aiStage, setAiStage] = useState<'describe' | 'review' | 'form'>('describe');

  // Removes the "AI-suggested" badge from a field once the user edits it
  const clearAiBadge = (field: string) => {
    setAiFilledFields(prev => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const aiBadge = (field: string) =>
    aiFilledFields.has(field) ? (
      <span className="ml-1" title="AI-suggested — review before saving">✨</span>
    ) : null;

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.amount || isNaN(parseFloat(formData.amount))) {
      newErrors.amount = 'Please enter a valid amount';
    }
    
    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }
    
    if (!formData.accountId) {
      newErrors.accountId = 'Please select an account';
    }

    if (formData.type === 'transfer') {
      if (!formData.toAccountId) {
        newErrors.toAccountId = 'Please select a destination account';
      }
      if (formData.accountId === formData.toAccountId) {
        newErrors.toAccountId = 'Source and destination accounts must be different';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Memoize rules to prevent infinite loops when using in useEffect
  const memoizedRules = useMemo(() => {
    console.log('[RULES MEMO] Memoizing rules, count:', rules.length);
    return rules;
  }, [rules]);
  
  // Add a direct effect to log when rules change
  useEffect(() => {
    console.log('[RULES CHANGE] Rules updated:', {
      count: rules.length,
      rules: rules,
      activeCount: rules.filter(r => r.active).length
    });
  }, [rules]);
  
  useEffect(() => {
    console.log('[RULES EFFECT] useEffect triggered with:', {
      rulesCount: memoizedRules.length,
      description: formData.description,
      categoryId: formData.categoryId,
      hasRules: memoizedRules.length > 0,
      activeRulesCount: memoizedRules.filter(r => r.active).length
    });
    
    // Debug: Log all rules
    if (memoizedRules.length > 0) {
      console.log('[RULES EFFECT] Available rules:', memoizedRules);
      console.log('[RULES EFFECT] Active rules:', memoizedRules.filter(r => r.active));
    }
    
    // Only run if we have rules to apply AND we have content in any field that could match rules
    if (memoizedRules.length > 0 && (
      (formData.description && formData.description.trim() !== '') ||
      (formData.notes && formData.notes.trim() !== '') ||
      (formData.payee && formData.payee.trim() !== '') ||
      (formData.tags && formData.tags.length > 0)
    )) {
      console.log('Checking for rules to apply, fields:', {
        description: formData.description,
        notes: formData.notes,
        payee: formData.payee,
        tags: formData.tags
      });
      // Create a temporary transaction object for rules processing
      const tempTransaction = {
        description: formData.description || '',
        categoryId: formData.categoryId,
        payee: formData.payee,
        notes: formData.notes,
        tags: formData.tags || []
      };
      
      console.log('Temp transaction for rule application:', tempTransaction);
      
      // Apply rules and update form data
      const modifiedTransaction = applyRules(tempTransaction);
      console.log('Transaction after applying rules:', modifiedTransaction);
      
      // If the rule changed the categoryId, find the corresponding category name
      let categoryName = formData.category;
      if (modifiedTransaction.categoryId && modifiedTransaction.categoryId !== formData.categoryId) {
        // Find the category name from the ID
        let parentCategoryName = '';
        let foundCategoryName = '';
        
        for (const category of categories) {
          // Check if this is a main category
          if (category.id === modifiedTransaction.categoryId) {
            foundCategoryName = category.name;
            break;
          }
          
          // Check if this is a subcategory
          const subcategory = category.subcategories?.find((sub: { id: string }) => sub.id === modifiedTransaction.categoryId);
          if (subcategory) {
            foundCategoryName = subcategory.name;
            parentCategoryName = category.name;
            break;
          }
        }
        
        // For display purposes, we'll show the parent category name if this is a subcategory
        categoryName = parentCategoryName ? `${parentCategoryName} → ${foundCategoryName}` : foundCategoryName;
      }
      
      // Only update form data if the rules changed something
      if (
        modifiedTransaction.categoryId !== tempTransaction.categoryId ||
        modifiedTransaction.payee !== tempTransaction.payee ||
        modifiedTransaction.notes !== tempTransaction.notes ||
        JSON.stringify(modifiedTransaction.tags) !== JSON.stringify(tempTransaction.tags)
      ) {
        console.log('Rules changed the transaction, updating form');
        console.log('[RULES EFFECT] Form update details:', {
          oldCategoryId: tempTransaction.categoryId,
          newCategoryId: modifiedTransaction.categoryId,
          oldPayee: tempTransaction.payee,
          newPayee: modifiedTransaction.payee,
          oldNotes: tempTransaction.notes,
          newNotes: modifiedTransaction.notes,
          oldTags: tempTransaction.tags,
          newTags: modifiedTransaction.tags
        });
        setFormData(prev => ({
          ...prev,
          categoryId: modifiedTransaction.categoryId || prev.categoryId,
          category: categoryName || prev.category,
          payee: modifiedTransaction.payee || prev.payee,
          notes: modifiedTransaction.notes || prev.notes,
          tags: modifiedTransaction.tags || prev.tags
        }));
      } else {
        console.log('[RULES EFFECT] No changes from rules application');
      }
    } else {
      if (memoizedRules.length === 0) {
        console.log('[RULES EFFECT] No rules available to apply');
      } else {
        console.log('[RULES EFFECT] No content in any fields to match rules against');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.description, formData.notes, formData.categoryId, formData.payee, formData.tags, categories, memoizedRules]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setIsSubmitting(true);

      if (formData.type === 'transfer') {
        console.log('Handling transfer transaction with new transfer service');
        // Handle transfer transaction using the new transfer service
        if (!user?.id || !formData.accountId || !formData.toAccountId) {
          toast.error('Missing required fields for transfer');
          return;
        }

        const amount = parseFloat(formData.amount);
        const exchangeRate = formData.exchangeRate || 1;

        try {
          // Use the new transfer service for better transfer management
          await coreTransactionStore.createTransfer(user.id, {
            fromAccountId: formData.accountId,
            toAccountId: formData.toAccountId,
            amount: Math.abs(amount),
            exchangeRate: exchangeRate,
            date: formData.date,
            description: formData.description,
            notes: formData.notes,
            tags: formData.tags
          });
          
          toast.success('Transfer created successfully');
        } catch (error: any) {
          console.error('Error creating transfer:', error);
          toast.error(`Error creating transfer: ${error?.message || 'Unknown error'}`);
        }
      } else {
        console.log('Handling income or expense transaction');
        // Handle income or expense transaction
        if (!user?.id || !formData.accountId) {
          toast.error('Missing required fields');
          return;
        }

        const amount = parseFloat(formData.amount);

        try {
          // Create regular transaction data with guaranteed valid IDs
          const fromAccount = accounts.find(acc => acc.id === formData.accountId);
          
          // Create transaction object
          let transactionData = {
            // For expenses, ensure the amount is negative
            amount: formData.type === 'expense' ? -Math.abs(amount) : Math.abs(amount),
            date: formData.date,
            description: formData.description || '',
            account_id: formData.accountId,
            category_id: formData.categoryId || undefined,
            payee_id: formData.payee || undefined,
            payee_name: formData.payeeName || '',
            status: 'completed' as const,
            type: formData.type as 'income' | 'expense',
            tags: formData.tags || [],
            notes: formData.notes || '',
            // Use the account's currency instead of hardcoding USD
            currency: fromAccount?.currency || 'USD',
            metadata: {
              hasAttachment: formData.hasAttachment || false,
              attachments: formData.attachments || []
            }
          };
          
          // OPTIMIZATION: Skip redundant rule application (already applied in useEffect)
          // Rules are already applied during form changes, no need to re-apply here
          console.log('Submitting transaction with pre-applied rules:', transactionData);
          
          console.log('Submitting final transaction:', transactionData);
          
          // Check for duplicates before adding (using optimized version)
          try {
            const duplicates = await checkForDuplicatesOptimized(user.id, transactionData);
            
            if (duplicates.isDuplicate) {
              // Show duplicate confirmation modal
              setDuplicateResult(duplicates);
              setPendingTransaction(transactionData);
              setShowDuplicateModal(true);
              return; // Don't submit yet, wait for user confirmation
            }
          } catch (error) {
            console.error('Error checking for duplicates:', error);
            // Continue with transaction if duplicate check fails
          }
          
          await coreTransactionStore.addTransaction(user.id, transactionData);
          
          toast.success(`${formData.type === 'income' ? 'Income' : 'Expense'} added successfully`);
        } catch (error: any) {
          console.error('Error adding transaction:', error);
          toast.error(`Error adding transaction: ${error?.message || 'Unknown error'}`);
        }
      }

      // Force refresh the transaction list to ensure UI shows latest updates
      try {
        console.log('Transaction added successfully, refreshing transaction list...');
        if (user?.id) {
          await fetchTransactions(user.id, {
            page: 1,
            pageSize: 200,
            orderBy: 'date',
            orderDirection: 'desc'
          });
          console.log('Transaction list refreshed after adding transaction');
        }
      } catch (refreshError) {
        console.error('Error refreshing transaction list after adding transaction:', refreshError);
      }
      
      // Reset form and close dialog
      resetForm();
      if (onClose) onClose();
      
      // Invalidate queries to refresh data
      invalidateTransactions(queryClient);
    } catch (error) {
      console.error('Error adding transaction:', error);
      toast.error('Failed to add transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewPayee = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      // @ts-ignore - Type mismatch between Firebase and Supabase
      const newPayee = {
        name: newPayeeName,
        user_id: user.id,
        category: formData.category || '',
        type: 'personal'
      };

      try {
        // First add payee using core store
        await addPayee(newPayee);
        
        // Then manually create a payee object with the new name
        // This avoids having to fetch the entire list again
        const newPayeeObj: Payee = {
          id: Date.now().toString(), // Use temporary ID
          name: newPayeeName,
          type: 'personal',
          user_id: user.id,
          category: formData.category || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setNewPayeeName('');
        setFormData({
          ...formData,
          payee: newPayeeObj.id,
          payeeName: newPayeeObj.name
        });
        toast.success(`Payee "${newPayeeObj.name}" added successfully`);
        
        // Refresh payees list in the background
        fetchPayees(user.id).catch(err => {
          console.error('Error refreshing payees list:', err);
        });
      } catch (error) {
        console.error('Error adding payee:', error);
        toast.error('Failed to add payee');
      }
    } catch (error) {
      console.error('Error adding payee:', error);
      toast.error('Failed to add payee');
    }
  };

  const handleAddNewTag = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;

    try {
      // @ts-ignore - Property names differ between Firebase and Supabase
      const newTagData = {
        user_id: user.id,
        name: newTagName,
        color: selectedColor
      };

      try {
        // First add tag using core store
        await addTag(newTagData);
        
        // Then manually create a tag object with the new name and color
        // This avoids having to fetch the entire list again
        const newTagObj: Tag = {
          id: Date.now().toString(), // Use temporary ID
          name: newTagName,
          color: selectedColor,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        setNewTagName('');
        setFormData({
          ...formData,
          tags: [...formData.tags, newTagObj.id]
        });
        toast.success(`Tag "${newTagObj.name}" added successfully`);
        
        // Refresh tags list in the background
        fetchTags(user.id).catch(err => {
          console.error('Error refreshing tags list:', err);
        });
      } catch (error) {
        console.error('Error adding tag:', error);
        toast.error('Failed to add tag');
      }
    } catch (error) {
      console.error('Error adding tag:', error);
      toast.error('Failed to add tag');
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    console.log('Selected category ID:', categoryId);
    let categoryName = '';
    let parentCategoryName = '';
    
    // Find the category name from the ID
    for (const category of categories) {
      // Check if this is a main category
      if (category.id === categoryId) {
        categoryName = category.name;
        break;
      }
      
      // Check if this is a subcategory
      const subcategory = category.subcategories?.find((sub: { id: string; name: string }) => sub.id === categoryId);
      if (subcategory) {
        categoryName = subcategory.name;
        parentCategoryName = category.name;
        break;
      }
    }

    // For display purposes, we'll show the parent category name if this is a subcategory
    const displayName = parentCategoryName ? `${parentCategoryName} → ${categoryName}` : categoryName;

    setFormData(prev => ({
      ...prev,
      category: displayName,
      categoryId: categoryId
    }));
    clearAiBadge('category');

    if (errors.category) {
      setErrors(prev => ({ ...prev, category: '' }));
    }
    
    setCategoryQuery('');
    setIsCategoryOpen(false);
  };

  const handlePayeeChange = (payeeId: string) => {
    // Find the payee name from the payees array
    const selectedPayee = payees.find(p => p.id === payeeId);
    setFormData(prev => ({
      ...prev,
      payee: payeeId, // Store the payeeId
      payeeName: selectedPayee?.name || '' // Store the payee name
    }));
    clearAiBadge('payee');
    if (errors.payee) {
      setErrors(prev => ({ ...prev, payee: '' }));
    }
    setIsPayeeOpen(false);
    setPayeeQuery('');
  };

  // Applies a parsed result (from the text parser or the receipt scanner) to
  // the form. Purely additive: never overwrites fields the user already
  // filled, and never auto-submits. The existing rules-engine useEffect above
  // re-runs on the filled description/payee, so user-defined rules stay
  // authoritative over the AI's category guess.
  const applyParsedResult = (parsed: NonNullable<Awaited<ReturnType<typeof parseTransactionText>>['data']>) => {
    // Resolve the category display name (categories are hierarchical on web)
    let categoryName = '';
    let parentCategoryName = '';
    if (parsed.categoryId) {
      for (const category of categories) {
        if (category.id === parsed.categoryId) {
          categoryName = category.name;
          break;
        }
        const subcategory = category.subcategories?.find(
          (sub: { id: string; name: string }) => sub.id === parsed.categoryId
        );
        if (subcategory) {
          categoryName = subcategory.name;
          parentCategoryName = category.name;
          break;
        }
      }
    }
    const categoryDisplayName = parentCategoryName
      ? `${parentCategoryName} → ${categoryName}`
      : categoryName;

    const matchedPayee = parsed.payeeName
      ? payees.find(p => p.name?.toLowerCase() === parsed.payeeName!.toLowerCase())
      : undefined;

    const filled = new Set<string>();
    const next: FormData = { ...formData, type: parsed.type as TransactionType };

    if (!formData.amount && parsed.amount > 0) {
      next.amount = String(parsed.amount);
      filled.add('amount');
    }
    if (!formData.description && parsed.description) {
      next.description = parsed.description;
      filled.add('description');
    }
    if (!formData.categoryId && categoryName && parsed.type !== 'transfer') {
      next.categoryId = parsed.categoryId!;
      next.category = categoryDisplayName;
      filled.add('category');
    }
    if (parsed.type === 'transfer') {
      next.categoryId = '';
      next.category = '';
    }
    if (parsed.accountId) {
      // Only set when the user explicitly named an account in the text
      next.accountId = parsed.accountId;
      filled.add('account');
    }
    if (!formData.payee && !formData.payeeName && parsed.payeeName) {
      next.payee = matchedPayee?.id || '';
      next.payeeName = matchedPayee?.name || parsed.payeeName;
      filled.add('payee');
    }
    if (parsed.date) {
      next.date = `${parsed.date}T12:00`;
      filled.add('date');
    }

    setFormData(next);
    setAiFilledFields(filled);
    setAiBanner(parsed.confidence === 'low' ? 'low' : 'success');
    if (errors.amount) setErrors(prev => ({ ...prev, amount: '' }));

    const targetAccount = accounts.find(
      (acc: Account) => acc.id === (parsed.accountId || formData.accountId)
    );
    if (targetAccount?.currency && targetAccount.currency !== parsed.currency) {
      setAiCurrencyHint(
        `Heads up: you mentioned ${parsed.currency}, but the selected account is in ${targetAccount.currency}.`
      );
    }

    setAiStage('review');
  };

  // Parse the natural-language description via the parse-transaction Edge
  // Function and pre-fill the form.
  const handleAiParse = async () => {
    const text = aiInput.trim();
    if (text.length < 4 || aiParsing) return;

    setAiParsing(true);
    setAiError(null);
    setAiBanner(null);
    setAiCurrencyHint(null);

    const { data: parsed, error } = await parseTransactionText(text);
    setAiParsing(false);

    if (error || !parsed) {
      if (error?.error === 'rate_limited') {
        setAiError('Too many requests — try again shortly.');
      } else if (error?.error === 'input_too_long') {
        setAiError('That description is too long — keep it under 500 characters.');
      } else {
        setAiError("Couldn't parse that — fill in the details below.");
      }
      return;
    }

    applyParsedResult(parsed);
  };

  // Scan a receipt / bank-SMS screenshot via the parse-receipt Edge Function.
  // Reuses the exact same additive fill + review flow as the text parser, and
  // attaches the original photo to the transaction through the existing
  // pre-save attachment pipeline (removable in the form like any attachment).
  const handleReceiptParse = async (file: File) => {
    if (aiParsing) return;

    setAiParsing(true);
    setAiError(null);
    setAiBanner(null);
    setAiCurrencyHint(null);

    const compressed = await compressReceiptImage(file);
    if ('error' in compressed) {
      setAiParsing(false);
      setAiError(
        compressed.error === 'image_too_large'
          ? 'That image is too large — try one under 15MB.'
          : "Couldn't read that image — try a clearer photo."
      );
      return;
    }

    const { data: parsed, error } = await parseReceiptImage(
      compressed.base64,
      compressed.mediaType
    );
    setAiParsing(false);

    if (error || !parsed) {
      if (error?.error === 'rate_limited') {
        setAiError('Too many requests — try again shortly.');
      } else if (error?.error === 'not_a_receipt') {
        setAiError("That doesn't look like a receipt or payment screenshot.");
      } else if (error?.error === 'image_too_large' || error?.error === 'unsupported_image') {
        setAiError("Couldn't read that image — try a clearer photo.");
      } else {
        setAiError("Couldn't scan that — fill in the details below.");
      }
      return;
    }

    applyParsedResult(parsed);

    // Attach the original photo in the background; non-critical if it fails.
    if (user) {
      uploadAttachment(user.id, 'temp', file)
        .then((uploaded) => {
          if (!uploaded) return;
          const now = new Date();
          const attachment: Attachment = {
            id: uploaded.id,
            name: file.name,
            type: file.type,
            url: uploaded.fileUrl || '',
            fileName: uploaded.fileName,
            fileUrl: uploaded.fileUrl || '',
            fileType: uploaded.fileType || 'image/jpeg',
            uploadedAt: now,
            size: uploaded.size || 0,
            userId: user.id,
            createdAt: now,
            updatedAt: now,
          };
          setFormData(prev => ({
            ...prev,
            attachments: [...(prev.attachments || []), attachment],
            hasAttachment: true,
          }));
        })
        .catch((err) => console.error('Receipt attachment upload failed:', err));
    }
  };

  // Save directly from the review card; falls back to the form if something
  // required is still missing so the user can see the validation errors.
  const handleReviewSave = () => {
    if (!validateForm()) {
      setAiStage('form');
      return;
    }
    handleTransactionFormSubmit();
  };

  const handleStartOver = () => {
    handleAiClear();
    setAiStage('describe');
  };

  // Resets everything the AI filled back to blank/manual defaults
  const handleAiClear = () => {
    setFormData(prev => ({
      ...prev,
      amount: aiFilledFields.has('amount') ? '' : prev.amount,
      description: aiFilledFields.has('description') ? '' : prev.description,
      category: aiFilledFields.has('category') ? '' : prev.category,
      categoryId: aiFilledFields.has('category') ? '' : prev.categoryId,
      payee: aiFilledFields.has('payee') ? '' : prev.payee,
      payeeName: aiFilledFields.has('payee') ? '' : prev.payeeName,
      accountId: aiFilledFields.has('account')
        ? defaultAccount?.id || prev.accountId
        : prev.accountId,
      date: aiFilledFields.has('date')
        ? format(new Date(), "yyyy-MM-dd'T'HH:mm")
        : prev.date,
    }));
    setAiFilledFields(new Set());
    setAiBanner(null);
    setAiCurrencyHint(null);
    setAiError(null);
    setAiInput('');
  };

  const filteredCategories = categoryQuery === ''
    ? categories.filter(category => category.type === formData.type)
    : categories
        .filter(category => category.type === formData.type)
        .filter(category => {
          const matchInCategory = category.name.toLowerCase().includes(categoryQuery.toLowerCase());
          const matchInSubcategories = category.subcategories?.some(
            (sub: { name: string }) => sub.name.toLowerCase().includes(categoryQuery.toLowerCase())
          );
          return matchInCategory || matchInSubcategories;
        });

  const filteredPayees = payeeQuery === ''
    ? payees
    : payees.filter(payee =>
        payee.name.toLowerCase().includes(payeeQuery.toLowerCase())
      );

  const colorOptions = [
    { id: 'blue', name: 'Blue', value: '#3B82F6' },
    { id: 'indigo', name: 'Indigo', value: '#6366F1' },
    { id: 'purple', name: 'Purple', value: '#8B5CF6' },
    { id: 'pink', name: 'Pink', value: '#EC4899' },
    { id: 'red', name: 'Red', value: '#EF4444' },
    { id: 'orange', name: 'Orange', value: '#F97316' },
    { id: 'amber', name: 'Amber', value: '#F59E0B' },
    { id: 'yellow', name: 'Yellow', value: '#EAB308' },
    { id: 'lime', name: 'Lime', value: '#84CC16' },
    { id: 'green', name: 'Green', value: '#22C55E' },
    { id: 'emerald', name: 'Emerald', value: '#10B981' },
    { id: 'teal', name: 'Teal', value: '#14B8A6' },
    { id: 'cyan', name: 'Cyan', value: '#06B6D4' },
    { id: 'sky', name: 'Sky', value: '#0EA5E9' },
    { id: 'violet', name: 'Violet', value: '#7C3AED' },
    { id: 'fuchsia', name: 'Fuchsia', value: '#D946EF' },
    { id: 'rose', name: 'Rose', value: '#F43F5E' },
    { id: 'slate', name: 'Slate', value: '#64748B' },
  ];

  const handleAttachmentChange = (attachments: Attachment[]) => {
    setFormData(prev => ({
      ...prev,
      attachments,
    }));
  };

  const resetForm = () => {
    setFormData({
      amount: '',
      date: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
      description: '',
      category: '',
      categoryId: '',
      accountId: defaultAccount?.id || '',
      payee: '',
      payeeName: '',
      notes: '',
      tags: [],
      attachments: [],
      hasAttachment: false,
      type: 'expense',
      status: 'completed',
      exchangeRate: 1
    });
  };

  // Check if we have any accounts
  const hasAccounts = Boolean(accounts?.length);

  // Update the useEffect that handles modal visibility
  useEffect(() => {
    if (!onClose && !accountsLoading) {
      if (!hasAccounts && isModalOpen) {
        console.log('No accounts found, closing modal');
        setIsModalOpen(false);
        toast.error("No Accounts Found", {
          description: (
            <div className="mt-2 flex flex-col space-y-2">
              <p>Please add at least one account before creating a transaction.</p>
              <button 
                className="text-[#439476] hover:text-[#387565] font-medium text-left flex items-center"
                onClick={() => {
                  window.location.href = '/accounts';
                }}
              >
                Go to Accounts <span className="ml-1">→</span>
              </button>
            </div>
          ),
          duration: 5000,
        });
      }
    }
  }, [onClose, hasAccounts, accountsLoading, isModalOpen]);

  // Add a separate effect to handle initial state
  useEffect(() => {
    if (!onClose && !accountsLoading && !hasAccounts) {
      setIsModalOpen(false);
    }
  }, [onClose, accountsLoading, hasAccounts]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup attachments when component unmounts
      if (formData.attachments.length > 0 && user) {
        formData.attachments.forEach(attachment => {
          deleteTransactionAttachment(user.id, uuidv4(), attachment)
            .catch(error => console.error('Error cleaning up attachment:', error));
        });
      }
    };
  }, []);

  const handleClose = () => {
    // Cleanup attachments when closing without submitting
    if (formData.attachments.length > 0 && user) {
      formData.attachments.forEach(attachment => {
        deleteTransactionAttachment(user.id, uuidv4(), attachment)
          .catch(error => console.error('Error cleaning up attachment:', error));
      });
    }
    onClose?.();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.combobox-container')) {
        setIsCategoryOpen(false);
      }
    };

    if (isCategoryOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isCategoryOpen]);

  // Create wrapper functions to handle form submissions
  const handleTransactionFormSubmit = () => {
    const form = document.getElementById('addTransactionForm') as HTMLFormElement;
    if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handlePayeeFormSubmit = () => {
    const form = document.getElementById('addPayeeForm') as HTMLFormElement;
    if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handleTagFormSubmit = () => {
    const form = document.getElementById('addTagForm') as HTMLFormElement;
    if (form) form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const handleDuplicateConfirm = async (forceAdd: boolean) => {
    setShowDuplicateModal(false);
    
    if (!forceAdd) {
      // User canceled, don't add the transaction
      setPendingTransaction(null);
      setDuplicateResult(null);
      setIsSubmitting(false);
      return;
    }

    // User wants to add anyway, proceed with transaction
    if (pendingTransaction && user?.id) {
      try {
        await coreTransactionStore.addTransaction(user.id, pendingTransaction);
        toast.success(`${pendingTransaction.type === 'income' ? 'Income' : 'Expense'} added successfully`);
        
        // Force refresh the transaction list to ensure UI shows latest updates
        try {
          console.log('Transaction added after duplicate confirmation, refreshing transaction list...');
          if (user?.id) {
            await fetchTransactions(user.id, {
              page: 1,
              pageSize: 200,
              orderBy: 'date',
              orderDirection: 'desc'
            });
            console.log('Transaction list refreshed after duplicate confirmation');
          }
        } catch (refreshError) {
          console.error('Error refreshing transaction list after duplicate confirmation:', refreshError);
        }
        
        // Reset form and close dialog
        resetForm();
        if (onClose) onClose();
        
        // Invalidate queries to refresh data
        invalidateTransactions(queryClient);
      } catch (error: any) {
        console.error('Error adding transaction after duplicate confirmation:', error);
        toast.error(`Error adding transaction: ${error?.message || 'Unknown error'}`);
      } finally {
        setPendingTransaction(null);
        setDuplicateResult(null);
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      {!onClose && (
        <button
          onClick={() => {
            console.log('Add Transaction button clicked');
            console.log('Current state:', {
              user: user?.id,
              accountsLoading,
              hasAccounts,
              accounts: accounts?.length
            });

            if (!user?.id) {
              console.log('No user found, showing auth toast');
              toast.error("Authentication Required", {
                description: "Please sign in to add transactions.",
              });
              return;
            }
            
            if (accountsLoading) {
              console.log('Accounts still loading');
              return;
            }

            if (!hasAccounts) {
              console.log('No accounts found, showing toast');
              toast.error("No Accounts Found", {
                description: (
                  <div className="mt-2 flex flex-col space-y-2">
                    <p>Please add at least one account before creating a transaction.</p>
                    <button 
                      className="text-[#439476] hover:text-[#387565] font-medium text-left flex items-center"
                      onClick={() => {
                        window.location.href = '/accounts';
                      }}
                    >
                      Go to Accounts <span className="ml-1">→</span>
                    </button>
                  </div>
                ),
              });
              return;
            }

            console.log('Opening modal');
            setIsModalOpen(true);
          }}
          className="fixed bottom-6 right-6 flex items-center justify-center w-14 h-14 rounded-full bg-primary dark:bg-primary-dark text-white hover:bg-primary/90 dark:hover:bg-primary-dark/90 focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark focus:ring-offset-2 transition-all duration-200 ease-in-out shadow-lg hover:shadow-xl z-[1000]"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}

      {((onClose && hasAccounts && !accountsLoading) || (!onClose && isModalOpen && hasAccounts && !accountsLoading)) && (
        <StyledModal
          isOpen={true}
          onClose={handleClose}
          title="Add Transaction"
          maxWidth="900px"
          footer={
            aiStage === 'describe' ? (
              <StyledModalButton onClick={handleClose} variant="outline">
                Cancel
              </StyledModalButton>
            ) : aiStage === 'review' ? (
              <>
                <StyledModalButton
                  onClick={handleReviewSave}
                  disabled={isSubmitting}
                  fullWidth
                  variant="primary"
                >
                  {isSubmitting ? 'Adding...' : 'Save Transaction'}
                </StyledModalButton>
                <StyledModalButton
                  onClick={() => setAiStage('form')}
                  variant="outline"
                  disabled={isSubmitting}
                >
                  Edit details
                </StyledModalButton>
              </>
            ) : (
              <>
                <StyledModalButton
                  onClick={handleTransactionFormSubmit}
                  disabled={isSubmitting}
                  fullWidth
                  variant="primary"
                >
                  {isSubmitting ? 'Adding...' : 'Add Transaction'}
                </StyledModalButton>
                <StyledModalButton
                  onClick={handleClose}
                  variant="outline"
                  disabled={isSubmitting}
                >
                  Cancel
                </StyledModalButton>
              </>
            )
          }
        >
          {/* Stage 0: AI-first natural-language entry */}
          {aiStage === 'describe' && (
            <div className="py-4 flex flex-col items-center">
              <div className="w-full rounded-xl border border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-secondary dark:text-primary-dark shrink-0" />
                  <h3 className="text-base font-medium text-text-primary dark:text-text-dark-primary">
                    What did you spend or receive?
                  </h3>
                </div>
                <textarea
                  autoFocus
                  rows={3}
                  value={aiInput}
                  onChange={(e) => {
                    setAiInput(e.target.value);
                    if (aiError) setAiError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAiParse();
                    }
                  }}
                  placeholder="e.g. spent 200 TL on groceries at BIM"
                  className="block w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors resize-none"
                  disabled={aiParsing}
                  maxLength={500}
                />
                <p className="mt-2 text-xs text-text-secondary dark:text-text-dark-secondary">
                  Try: "dün Migros'tan 350 lira harcadım" or "received $1200 salary"
                </p>
                {aiError && <p className="mt-2 text-xs text-error">{aiError}</p>}
                <button
                  type="button"
                  onClick={handleAiParse}
                  disabled={aiInput.trim().length < 4 || aiParsing}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-xl bg-primary dark:bg-primary-dark text-white disabled:opacity-40 hover:bg-primary/90 dark:hover:bg-primary-dark/90 transition-colors"
                >
                  {aiParsing && <Loader2 className="w-4 h-4 animate-spin" />}
                  {aiParsing ? 'Parsing…' : 'Parse'}
                </button>
                <label
                  className={`mt-2 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-medium rounded-xl border border-border dark:border-border-dark text-text-primary dark:text-text-dark-primary transition-colors ${
                    aiParsing ? 'opacity-40 pointer-events-none' : 'cursor-pointer hover:bg-background-secondary dark:hover:bg-background-dark-secondary'
                  }`}
                >
                  <Camera className="w-4 h-4" />
                  Scan a receipt instead
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={aiParsing}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) handleReceiptParse(file);
                    }}
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => setAiStage('form')}
                className="mt-4 text-sm text-secondary dark:text-primary-dark hover:underline"
              >
                Enter manually instead
              </button>
            </div>
          )}

          {/* Stage 1: review the parsed result before saving */}
          {aiStage === 'review' && (() => {
            const reviewAccount = accounts.find((acc: Account) => acc.id === formData.accountId);
            const typeStyles =
              formData.type === 'expense'
                ? 'bg-[#E53935]/15 text-[#E53935]'
                : 'bg-[#439476]/15 text-[#439476]';
            const reviewRows: [string, string, string][] = [
              ...(formData.type !== 'transfer'
                ? [['Category', formData.category || '—', 'category'] as [string, string, string]]
                : []),
              ['Account', reviewAccount?.name || '—', 'account'],
              ['Payee', formData.payeeName || '—', 'payee'],
              ['Date', formData.date ? format(new Date(formData.date), 'PPP') : '—', 'date'],
              ['Description', formData.description || '—', 'description'],
            ];
            return (
              <div className="py-2">
                <p
                  className={`text-xs mb-3 ${
                    aiBanner === 'low'
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-primary dark:text-primary-dark'
                  }`}
                >
                  {aiBanner === 'low'
                    ? '✨ Rough guess — please check every field before saving'
                    : '✨ Parsed from your description — review before saving'}
                </p>
                {aiCurrencyHint && (
                  <p className="text-xs mb-3 text-amber-600 dark:text-amber-400">{aiCurrencyHint}</p>
                )}
                <div className="rounded-xl border border-border dark:border-border-dark bg-background-secondary/50 dark:bg-background-dark-secondary/50 p-4">
                  <div className="flex flex-col items-center pb-4">
                    <span className={`px-2.5 py-1 rounded-lg text-xs font-medium capitalize ${typeStyles}`}>
                      {formData.type}
                    </span>
                    <span className="mt-2 text-3xl font-bold text-text-primary dark:text-text-dark-primary">
                      {getCurrencySymbol(accounts, formData.accountId)}{formData.amount || '0'}
                    </span>
                  </div>
                  {reviewRows.map(([label, value, key]) => (
                    <div
                      key={key}
                      className="flex items-center justify-between py-2.5 border-t border-border dark:border-border-dark"
                    >
                      <span className="text-xs text-text-secondary dark:text-text-dark-secondary">
                        {label}{aiFilledFields.has(key) ? ' ✨' : ''}
                      </span>
                      <span className="text-sm text-text-primary dark:text-text-dark-primary text-right ml-4 truncate max-w-[60%]">
                        {value}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={handleStartOver}
                    className="text-xs text-text-secondary dark:text-text-dark-secondary hover:underline"
                  >
                    Start over
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Stage 2: full form — stays mounted (hidden) so the footer's
              dispatched submit event always has a form to target */}
          <form
            id="addTransactionForm"
            onSubmit={handleSubmit}
            className={aiStage === 'form' ? 'space-y-3' : 'hidden'}
          >
            <div className="flex gap-3 p-1 bg-[#F3F4F6] dark:bg-[#1E2B42]/50 rounded-xl">
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ 
                    ...prev, 
                    type: 'expense',
                    toAccountId: '',
                    exchangeRate: 1
                  }));
                }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.type === 'expense'
                    ? 'bg-[#E53935] text-white shadow-sm'
                    : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
                }`}
                disabled={isSubmitting}
              >
                Expense
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ 
                    ...prev, 
                    type: 'income',
                    toAccountId: '',
                    exchangeRate: 1
                  }));
                }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.type === 'income'
                    ? 'bg-[#439476] text-white shadow-sm'
                    : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
                }`}
                disabled={isSubmitting}
              >
                Income
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({ 
                    ...prev, 
                    type: 'transfer' as TransactionType,
                    category: '',
                    categoryId: ''
                  }));
                }}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                  formData.type === 'transfer'
                    ? 'bg-[#439476] text-white shadow-sm'
                    : 'text-[#2F2F2F] dark:text-[#A3A3A3] hover:bg-white/50 dark:hover:bg-[#1E2B42]/50'
                }`}
                disabled={isSubmitting}
              >
                Transfer
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                  Amount{aiBadge('amount')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <span className="text-text-secondary dark:text-text-dark-secondary sm:text-sm">{getCurrencySymbol(accounts, formData.accountId)}</span>
                  </div>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => {
                      setFormData({ ...formData, amount: e.target.value });
                      clearAiBadge('amount');
                      if (errors.amount) {
                        setErrors({ ...errors, amount: '' });
                      }
                    }}
                    className={`block w-full pl-7 pr-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ${
                      errors.amount ? 'ring-error' : 'ring-border dark:ring-border-dark'
                    } placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right`}
                    placeholder="0.00"
                    required
                    disabled={isSubmitting}
                  />
                  {errors.amount && (
                    <p className="mt-1 text-xs text-error">{errors.amount}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                  Date{aiBadge('date')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  </div>
                  <input
                    type="datetime-local"
                    value={formData.date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, date: e.target.value }));
                      clearAiBadge('date');
                    }}
                    className={`block w-full pl-10 pr-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ${
                      errors.date ? 'ring-error' : 'ring-border dark:ring-border-dark'
                    } placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right`}
                    required
                    disabled={isSubmitting}
                  />
                  {errors.date && (
                    <p className="mt-1 text-xs text-error">{errors.date}</p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                  {formData.type === 'transfer' ? 'From Account' : 'Account'}{aiBadge('account')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  </div>
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-text-secondary dark:text-text-dark-secondary" />
                  </div>
                  <select
                    value={formData.accountId}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, accountId: e.target.value }));
                      clearAiBadge('account');
                      if (errors.accountId) {
                        setErrors(prev => ({ ...prev, accountId: '' }));
                      }
                    }}
                    className="block w-full pl-10 pr-10 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors appearance-none h-10 text-left rtl:text-right"
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select account</option>
                    {[...accounts].sort((a, b) => a.name.localeCompare(b.name)).map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                  {errors.accountId && (
                    <p className="mt-1 text-xs text-error">{errors.accountId}</p>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                Description (optional){aiBadge('description')}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => {
                  setFormData({ ...formData, description: e.target.value });
                  clearAiBadge('description');
                }}
                className="block w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors h-10 text-left rtl:text-right"
                placeholder="Enter description (optional)"
                disabled={isSubmitting}
              />
            </div>

            {formData.type === 'transfer' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                    To Account
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <select
                      value={formData.toAccountId}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, toAccountId: e.target.value }));
                        if (errors.toAccountId) {
                          setErrors(prev => ({ ...prev, toAccountId: '' }));
                        }
                      }}
                      className="block w-full pl-10 pr-10 py-2.5 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors appearance-none h-10 text-left rtl:text-right"
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select destination account</option>
                      {[...accounts]
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .filter(account => account.id !== formData.accountId)
                        .map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name}
                          </option>
                        ))}
                    </select>
                    {errors.toAccountId && (
                      <p className="mt-1 text-xs text-error">{errors.toAccountId}</p>
                    )}
                  </div>
                </div>

                {formData.accountId && formData.toAccountId && (
                  <div>
                    <ExchangeRateInput
                      fromAccountId={formData.accountId}
                      toAccountId={formData.toAccountId}
                      exchangeRate={formData.exchangeRate || 1}
                      onExchangeRateChange={(rate) => {
                        setFormData(prev => ({ ...prev, exchangeRate: rate }));
                      }}
                      onFetchLatestRate={async () => {
                        try {
                          if (!user?.id) {
                            toast.error("User not authenticated");
                            return;
                          }
                          
                          const fromAccount = accounts.find(acc => acc.id === formData.accountId);
                          const toAccount = accounts.find(acc => acc.id === formData.toAccountId);
                          const fromAccountCurrencyId = (fromAccount as any)?.currencyId;
                          const toAccountCurrencyId = (toAccount as any)?.currencyId;
                          const fromAccountCurrencyCode = fromAccount?.currency;
                          const toAccountCurrencyCode = toAccount?.currency;
                          
                          if (!fromAccountCurrencyCode || !toAccountCurrencyCode) {
                            toast.error("Currency information is missing for one or both accounts");
                            return;
                          }

                          const unsupportedCurrencies = ['IQD', 'IRR', 'SYP', 'VES'];
                          
                          if (unsupportedCurrencies.includes(fromAccountCurrencyCode) || 
                              unsupportedCurrencies.includes(toAccountCurrencyCode)) {
                            
                            let rate = 1;
                            
                            if (fromAccountCurrencyCode === 'USD' && toAccountCurrencyCode === 'IQD') {
                              rate = 1309.5;
                            } else if (fromAccountCurrencyCode === 'IQD' && toAccountCurrencyCode === 'USD') {
                              rate = 0.00076;
                            } else {
                              toast.warning(`Automatic rates for ${fromAccountCurrencyCode} to ${toAccountCurrencyCode} are not available. Please enter manually.`);
                              return;
                            }
                            
                            rate = Math.round(rate * 10000) / 10000;
                            
                            setFormData(prev => ({ ...prev, exchangeRate: rate }));
                            
                            toast.success(`Using approximate exchange rate: 1 ${fromAccountCurrencyCode} = ${rate} ${toAccountCurrencyCode}`);
                            return;
                          }

                          if (!exchangeRates) {
                            await fetchExchangeRates();
                          }

                          try {
                            const fromCurrency = currencies.find(c => 
                              c.id === fromAccountCurrencyId || 
                              c.code === fromAccountCurrencyCode
                            );
                            
                            const toCurrency = currencies.find(c => 
                              c.id === toAccountCurrencyId || 
                              c.code === toAccountCurrencyCode
                            );

                            const fromCurrencyCode = fromCurrency?.code || fromAccountCurrencyCode;
                            const toCurrencyCode = toCurrency?.code || toAccountCurrencyCode;
                            
                            if (!fromCurrencyCode || !toCurrencyCode) {
                              toast.error("Could not determine currencies for the accounts");
                              return;
                            }

                            // Try to use core store conversion first (supports precious metals)
                            let rate: number;
                            
                            // Ensure core store has rates
                            if (!coreRates || !coreRates.rates || Object.keys(coreRates.rates).length === 0) {
                              await updateCoreRates();
                            }
                            
                            try {
                              rate = coreConvertAmount(1, fromCurrencyCode, toCurrencyCode);
                            } catch (error) {
                              // Fallback to the legacy service
                              rate = convertCurrency(1, fromCurrencyCode, toCurrencyCode, exchangeRates || {});
                            }

                            if (!rate || isNaN(rate)) {
                              toast.error("Could not calculate exchange rate");
                              return;
                            }

                            const roundedRate = Math.round(rate * 1000000) / 1000000; // 6 decimal places for precious metals
                            
                            setFormData(prev => ({ ...prev, exchangeRate: roundedRate }));
                            
                            const preciousMetals = ['XAU', 'XAG', 'XPT', 'XPD', 'XRH'];
                            const fromUnit = preciousMetals.includes(fromCurrencyCode) ? `${fromCurrencyCode}/g` : fromCurrencyCode;
                            const toUnit = preciousMetals.includes(toCurrencyCode) ? `${toCurrencyCode}/g` : toCurrencyCode;
                            toast.success(`Exchange rate updated: 1 ${fromUnit} = ${roundedRate} ${toUnit}`);
                          } catch (error) {
                            console.error('Error calculating exchange rate:', error);
                            toast.error("Failed to calculate exchange rate. Please enter it manually.");
                          }
                        } catch (error) {
                          console.error('Error updating exchange rate:', error);
                          toast.error("Failed to update exchange rate");
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {formData.type !== 'transfer' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                    Category{aiBadge('category')}
                  </label>
                  <div className="relative">
                    <Combobox
                      as="div"
                      value={formData.categoryId}
                      onChange={handleCategorySelect}
                      className="relative"
                    >
                      <div className="relative">
                        <div
                          className="flex items-center bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl px-3 h-10 cursor-pointer hover:border-secondary dark:hover:border-secondary-dark transition-colors"
                          onClick={() => setIsCategoryOpen(true)}
                        >
                          <Tag className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2" />
                          <Combobox.Input
                            className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none text-text-primary dark:text-text-dark-primary placeholder-text-secondary dark:placeholder-text-dark-secondary appearance-none text-left rtl:text-right"
                            placeholder="Select category..."
                            onChange={(e) => setCategoryQuery(e.target.value)}
                            onBlur={() => setTimeout(() => setIsCategoryOpen(false), 200)}
                            displayValue={(categoryId: string) => {
                              if (!categoryId) return '';
                              return formData.category;
                            }}
                          />
                          <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2" />
                        </div>

                        <Transition
                          show={isCategoryOpen}
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Combobox.Options
                            static
                            className="absolute z-[70] mt-1 max-h-60 w-full overflow-auto rounded-md bg-background-secondary dark:bg-background-dark-secondary py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none text-sm"
                          >
                            {filteredCategories.length === 0 && categoryQuery !== '' ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-text-secondary dark:text-text-dark-secondary">
                                Nothing found.
                              </div>
                            ) : (
                              filteredCategories.map((category) => (
                                <Fragment key={category.id}>
                                  <Combobox.Option
                                    value={category.id}
                                    className={({ active }) =>
                                      `relative cursor-pointer select-none py-2 pl-4 pr-9 ${
                                        active
                                          ? 'bg-secondary/10 text-secondary'
                                          : 'text-text-primary dark:text-text-dark-primary'
                                      }`
                                    }
                                  >
                                    {({ selected, active }) => (
                                      <>
                                        <div className="flex items-center">
                                          <div
                                            className="w-2 h-2 rounded-full mr-2"
                                            style={{ backgroundColor: category.color }}
                                          />
                                          <span className={`block truncate ${selected ? 'font-bold' : ''}`}>
                                            {category.name}
                                          </span>
                                        </div>
                                        {selected && (
                                          <span
                                            className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                              active ? 'text-white' : 'text-secondary'
                                            }`}
                                          >
                                            <Check className="h-4 w-4" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>

                                  {category.subcategories?.map((subcategory: { id: string; name: string; color?: string }) => (
                                    <Combobox.Option
                                      key={subcategory.id}
                                      value={subcategory.id}
                                      className={({ active }) =>
                                        `relative cursor-pointer select-none py-2 pl-8 pr-9 ${
                                          active
                                            ? 'bg-secondary/10 text-secondary'
                                            : 'text-text-primary dark:text-text-dark-primary'
                                        }`
                                      }
                                    >
                                      {({ selected, active }) => (
                                        <>
                                          <div className="flex items-center">
                                            <div
                                              className="w-1.5 h-1.5 rounded-full mr-2"
                                              style={{ backgroundColor: category.color }}
                                            />
                                            <span className={`block truncate ${selected ? 'font-bold' : ''}`}>
                                              {subcategory.name}
                                            </span>
                                          </div>
                                          {selected && (
                                            <span
                                              className={`absolute inset-y-0 right-0 flex items-center pr-3 ${
                                                active ? 'text-white' : 'text-secondary'
                                              }`}
                                            >
                                              <Check className="h-4 w-4" aria-hidden="true" />
                                            </span>
                                          )}
                                        </>
                                      )}
                                    </Combobox.Option>
                                  ))}
                                </Fragment>
                              ))
                            )}
                          </Combobox.Options>
                        </Transition>
                      </div>
                    </Combobox>
                    {errors.category && (
                      <p className="mt-1 text-xs text-error">{errors.category}</p>
                    )}
                  </div>
                </div>

                                  <div>
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary text-left rtl:text-right">
                        Payee{aiBadge('payee')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsAddPayeeModalOpen(true)}
                        className="inline-flex items-center text-xs text-secondary dark:text-secondary-dark hover:text-primary dark:hover:text-primary-dark"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add New
                      </button>
                    </div>
                    <div className="relative payee-dropdown-container">
                      <div className="relative">
                        <div
                          className="flex items-center bg-background-secondary/50 dark:bg-background-dark-secondary/50 border border-border dark:border-border-dark rounded-xl px-3 h-10 cursor-pointer hover:border-secondary dark:hover:border-secondary-dark transition-colors"
                          onClick={handlePayeeOpen}
                        >
                          <User className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary mr-2" />
                          <input
                            type="text"
                            className="bg-transparent flex-1 outline-none border-none rounded-none focus:ring-0 focus:outline-none shadow-none focus:shadow-none text-text-primary dark:text-text-dark-primary placeholder-text-secondary dark:placeholder-text-dark-secondary appearance-none text-left rtl:text-right"
                            placeholder="Select payee..."
                            value={payeeQuery || formData.payeeName || ''}
                            onChange={(e) => {
                              setPayeeQuery(e.target.value);
                              setIsPayeeOpen(true);
                              // Clear the selected payee when typing
                              setFormData(prev => ({
                                ...prev,
                                payee: '',
                                payeeName: e.target.value
                              }));
                              clearAiBadge('payee');
                            }}
                            onFocus={() => setIsPayeeOpen(true)}
                          />
                          <ChevronDown className="w-4 h-4 text-text-secondary dark:text-text-dark-secondary ml-2" />
                        </div>

                        {isPayeeOpen && (
                          <div className="absolute z-[99999] w-full bg-background-secondary dark:bg-background-dark-secondary border border-gray-300 dark:border-gray-700 rounded-md mt-1 py-1 text-base shadow-lg max-h-60 overflow-auto focus:outline-none sm:text-sm">
                            {filteredPayees.length === 0 && payeeQuery !== '' ? (
                              <div className="relative cursor-default select-none py-2 px-4 text-text-secondary dark:text-text-dark-secondary">
                                Nothing found.
                              </div>
                            ) : (
                              filteredPayees.map((payee) => (
                                <div
                                  key={payee.id}
                                  className={`relative cursor-pointer select-none py-2 pl-4 pr-9 hover:bg-secondary/10 hover:text-secondary ${
                                    formData.payee === payee.id
                                      ? 'bg-secondary/10 text-secondary'
                                      : 'text-text-primary dark:text-text-dark-primary'
                                  }`}
                                  onClick={() => handlePayeeChange(payee.id)}
                                >
                                  <div className="flex items-center">
                                    <span className={`block truncate ${formData.payee === payee.id ? 'font-bold' : ''}`}>
                                      {payee.name}
                                    </span>
                                  </div>
                                  {formData.payee === payee.id && (
                                    <span
                                      className="absolute inset-y-0 right-0 flex items-center pr-3 text-secondary"
                                    >
                                      <Check className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                  Notes (optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="block w-full px-3 py-2 text-text-primary dark:text-text-dark-primary ring-1 ring-inset ring-border dark:ring-border-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark rounded-xl sm:text-sm bg-background-secondary/50 dark:bg-background-dark-secondary/50 transition-colors text-left rtl:text-right"
                  placeholder="Add any additional notes"
                  rows={2}
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary text-left rtl:text-right">
                    Tags
                  </label>
                  <button
                    type="button"
                    onClick={() => setIsAddTagModalOpen(true)}
                    className="inline-flex items-center text-xs text-secondary dark:text-secondary-dark hover:text-primary dark:hover:text-primary-dark"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add New
                  </button>
                </div>
                <TagSelect
                  value={formData.tags}
                  onChange={(tags) => setFormData({ ...formData, tags })}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1.5 text-left rtl:text-right">
                Attachments
              </label>
              {user && (
                <AttachmentUpload
                  userId={user.id}
                  transactionId="temp"
                  attachments={formData.attachments}
                  onAttachmentChange={handleAttachmentChange}
                  disabled={isSubmitting}
                />
              )}
            </div>
          </form>
        </StyledModal>
      )}
      {isAddPayeeModalOpen && (
        <StyledModal
          isOpen={true}
          onClose={() => setIsAddPayeeModalOpen(false)}
          title="Add New Payee"
          footer={
            <>
              <StyledModalButton
                onClick={handlePayeeFormSubmit}
                disabled={isAddingPayee || !newPayeeName.trim()}
                fullWidth
                variant="primary"
              >
                {isAddingPayee ? 'Adding...' : 'Add Payee'}
              </StyledModalButton>
              <StyledModalButton
                onClick={() => setIsAddPayeeModalOpen(false)}
                variant="outline"
                disabled={isAddingPayee}
              >
                Cancel
              </StyledModalButton>
            </>
          }
        >
          <form id="addPayeeForm" onSubmit={handleAddNewPayee} className="space-y-4">
            <div>
              <label htmlFor="payeeName" className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left rtl:text-right">
                Payee Name
              </label>
              <input
                id="payeeName"
                type="text"
                value={newPayeeName}
                onChange={(e) => setNewPayeeName(e.target.value)}
                className="block w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary bg-background-secondary/50 dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-xl focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary h-11 text-left rtl:text-right"
                placeholder="Enter payee name"
                required
                disabled={isAddingPayee}
              />
            </div>
          </form>
        </StyledModal>
      )}
      {isAddTagModalOpen && (
        <StyledModal
          isOpen={true}
          onClose={() => {
            setIsAddTagModalOpen(false);
            setNewTagName('');
            setSelectedColor(colorOptions[0].value);
          }}
          title="Add Tag"
          footer={
            <>
              <StyledModalButton
                onClick={handleTagFormSubmit}
                disabled={isAddingTag || !newTagName.trim()}
                fullWidth
                variant="primary"
              >
                {isAddingTag ? 'Adding...' : 'Add Tag'}
              </StyledModalButton>
              <StyledModalButton
                onClick={() => {
                  setIsAddTagModalOpen(false);
                  setNewTagName('');
                  setSelectedColor(colorOptions[0].value);
                }}
                variant="outline"
                disabled={isAddingTag}
              >
                Cancel
              </StyledModalButton>
            </>
          }
        >
          <form id="addTagForm" onSubmit={handleAddNewTag} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-1 text-left rtl:text-right">
                Name
              </label>
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                className="w-full px-3 py-2.5 text-text-primary dark:text-text-dark-primary bg-background-secondary/50 dark:bg-background-dark-secondary border border-border dark:border-border-dark rounded-xl focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark placeholder:text-text-secondary dark:placeholder:text-text-dark-secondary h-11 text-left rtl:text-right"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary dark:text-text-dark-primary mb-2 text-left rtl:text-right">
                Color
              </label>
              <div className="grid grid-cols-6 gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    onClick={() => setSelectedColor(color.value)}
                    className="relative w-8 h-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
                    style={{ backgroundColor: color.value }}
                  >
                    {selectedColor === color.value && (
                      <Check className="absolute inset-0 m-auto w-4 h-4 text-white" />
                    )}
                  </button>
                ))}
              </div>
              <div className="mt-2 flex items-center space-x-2">
                <div
                  className="w-6 h-6 rounded-full border border-border dark:border-border-dark"
                  style={{ backgroundColor: selectedColor }}
                />
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                  {colorOptions.find(c => c.value === selectedColor)?.name}
                </span>
              </div>
            </div>
          </form>
        </StyledModal>
      )}

      {/* Duplicate Confirmation Modal */}
      {duplicateResult && (
        <DuplicateConfirmation
          isOpen={showDuplicateModal}
          onClose={() => {
            setShowDuplicateModal(false);
            setPendingTransaction(null);
            setDuplicateResult(null);
            setIsSubmitting(false);
          }}
          onConfirm={handleDuplicateConfirm}
          duplicateResult={duplicateResult}
          newTransaction={pendingTransaction}
        />
      )}
    </>
  );
}